import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import axiosInstance from '../api/axiosInstance';
import logo from '../Assets/Img/Gold-logo.png';
import background from '../Assets/Img/bg3.png';

const SESSION_TIMEOUT = 12000;
const MIN_LOADER_DURATION = 1200;

function SessionScreen({ error, onRetry }) {
  return (
    <main className="relative min-h-screen flex items-center justify-center bg-gray-800 bg-cover bg-center px-6" style={{ backgroundImage: `url(${background})` }}>
      <div className="absolute inset-0 bg-gray-900/50" aria-hidden="true" />
      <section className="relative w-full max-w-md rounded-xl border border-white/60 bg-white/95 p-8 sm:p-10 text-center shadow-xl" aria-busy={!error}>
        <img src={logo} alt="FibreBond" className="mx-auto mb-5 h-16 w-16 rounded-full border border-gray-300 object-cover" />
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-[#94732b]">FibreBond MIS</p>
        <h1 className="text-2xl font-semibold text-gray-800">{error ? 'Connection interrupted' : 'Getting your workspace ready'}</h1>
        <p role={error ? 'alert' : 'status'} className="mt-3 text-sm leading-6 text-gray-600">
          {error || 'Please wait a moment.'}
        </p>
        {error ? (
          <button onClick={onRetry} className="mt-7 rounded-lg bg-gray-800 px-7 py-3 text-sm font-semibold text-white transition hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-amber-200">Retry</button>
        ) : (
          <div className="mt-7 flex justify-center" aria-hidden="true">
            <span className="h-7 w-7 animate-spin rounded-full border-[3px] border-amber-100 border-t-[#94732b] motion-reduce:animate-none" />
          </div>
        )}
      </section>
    </main>
  );
}

export default function ProtectedRoute() {
  const { accounts, inProgress } = useMsal();
  const location = useLocation();
  const [session, setSession] = useState('checking');
  const [attempt, setAttempt] = useState(0);
  const [minimumElapsed, setMinimumElapsed] = useState(false);
  const [microsoftTimedOut, setMicrosoftTimedOut] = useState(false);
  const isLocalhost = window.location.hostname === 'localhost';
  const microsoftAuthenticated = accounts.length > 0 || sessionStorage.getItem('msalLogin') === 'true';

  const waitingForMicrosoft = !isLocalhost && !microsoftAuthenticated && Boolean(inProgress && inProgress !== 'none');

  useEffect(() => {
    setMinimumElapsed(false);
    const timer = setTimeout(() => setMinimumElapsed(true), MIN_LOADER_DURATION);
    return () => clearTimeout(timer);
  }, [attempt]);

  useEffect(() => {
    setMicrosoftTimedOut(false);
    if (!waitingForMicrosoft) return;
    const timer = setTimeout(() => setMicrosoftTimedOut(true), SESSION_TIMEOUT);
    return () => clearTimeout(timer);
  }, [waitingForMicrosoft, attempt]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      active = false;
      controller.abort();
      setSession('error');
    }, SESSION_TIMEOUT);
    setSession('checking');
    // Cookies are shared between tabs; sessionStorage is not. Verify with the server.
    axiosInstance.get('/mis-dashboard', { skipSessionRedirect: true, timeout: SESSION_TIMEOUT, signal: controller.signal }).then(() => {
      if (!active) return;
      clearTimeout(timer);
      sessionStorage.setItem('auth', 'true');
      setSession('valid');
    }).catch(error => {
      if (!active) return;
      clearTimeout(timer);
      if ([401, 403].includes(error.response?.status)) {
        sessionStorage.removeItem('auth');
        setSession('invalid');
      } else {
        setSession('error');
      }
    });
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [attempt]);

  // Error states must not be hidden behind a Microsoft startup spinner.
  if (session === 'error' || microsoftTimedOut) {
    return <SessionScreen error={microsoftTimedOut
      ? 'Microsoft sign-in is taking longer than expected. Please retry.'
      : 'We could not verify your session. Check your connection and try again.'}
      onRetry={() => {
        if (microsoftTimedOut) window.location.reload();
        else setAttempt(value => value + 1);
      }} />;
  }
  if (session === 'checking' || waitingForMicrosoft || (session === 'valid' && !minimumElapsed)) return <SessionScreen />;
  if (session === 'valid' && (isLocalhost || microsoftAuthenticated)) return <Outlet />;
  return <Navigate to={isLocalhost || microsoftAuthenticated ? '/login' : '/auth'} state={{ from: location }} replace />;
}
